import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AuthContext } from '../App';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import CalculatorTutorial from '../components/CalculatorTutorial';
import HeadlineMetric from '../components/HeadlineMetric';
import PageHero from '../components/PageHero';
import ToolSelector from '../components/ToolSelector';
import { ShieldCheck } from 'lucide-react';
import { 
 Calculator, ClipboardList, Plus, Trash2, Save, Scale, 
 Package, TrendingUp, AlertCircle, CheckCircle, Loader, Sparkles
} from 'lucide-react';

const BatchTracker = () => {
 const navigate = useNavigate();
 const { API } = useContext(AuthContext);
 const [searchParams] = useSearchParams();
 
 // State
 const [batchName, setBatchName] = useState('');
 const [freezeDryerModels, setFreezeDryerModels] = useState([]);
 const [selectedModel, setSelectedModel] = useState(null);
 const [foodCategories, setFoodCategories] = useState({});
 const [trays, setTrays] = useState([]);
 const [loading, setLoading] = useState(false);
 const [previousBatches, setPreviousBatches] = useState([]);
 const [showImportModal, setShowImportModal] = useState(false);
 const [showTrayAssignmentModal, setShowTrayAssignmentModal] = useState(false);
 const [itemsToAssign, setItemsToAssign] = useState([]);
 const [weightUnit, setWeightUnit] = useState('g'); // g, lbs, oz, kg
 const [savedBatchTrackers, setSavedBatchTrackers] = useState([]);
 const [showLoadPreviousModal, setShowLoadPreviousModal] = useState(false);
 
 // Fetch initial data
 useEffect(() => {
 const fetchData = async () => {
 try {
 const [modelsRes, categoriesRes] = await Promise.all([
 axios.get(`${API}/calculator/freeze-dryer-models`),
 axios.get(`${API}/household/food-categories`)
 ]);
 
 setFreezeDryerModels(modelsRes.data.models || []);
 setFoodCategories(categoriesRes.data.categories || {});
 } catch (error) {
 console.error('Failed to fetch data:', error);
 toast.error('Failed to load calculator data');
 }
 };
 fetchData();
 }, [API]);
 
 // Fetch previous batches from both calculators
 useEffect(() => {
 const fetchPreviousBatches = async () => {
 try {
 const [businessRes, householdRes] = await Promise.all([
 axios.get(`${API}/calculator/batch-logs`),
 axios.get(`${API}/household/batch-logs`)
 ]);
 
 const businessBatches = (businessRes.data.logs || []).map(log => ({
 ...log,
 source: 'business',
 display_name: `${log.plan_name || log.inputs?.plan_name || 'Unnamed'} (Business)`
 }));
 
 const householdBatches = (householdRes.data.logs || []).map(log => ({
 ...log,
 source: 'household',
 display_name: `${log.inputs.batch_name} (Household)`
 }));
 
 setPreviousBatches([...businessBatches, ...householdBatches]);
 } catch (error) {
 console.error('Error fetching previous batches:', error);
 }
 };
 fetchPreviousBatches();
 }, [API]);
 
 // Fetch saved batch trackers
 useEffect(() => {
 const fetchSavedTrackers = async () => {
 try {
 const response = await axios.get(`${API}/batch-tracker/logs`);
 setSavedBatchTrackers(response.data.logs || []);
 } catch (error) {
 console.error('Error fetching saved batch trackers:', error);
 }
 };
 fetchSavedTrackers();
 }, [API]);
 
 // Load batch from URL parameter (when coming from Batch History)
 useEffect(() => {
 const loadFromUrl = async () => {
 const batchId = searchParams.get('load');
 if (batchId && freezeDryerModels.length > 0) {
 try {
 setLoading(true);
 const response = await axios.get(`${API}/batch-tracker/${batchId}`);
 if (response.data) {
 loadSavedTracker(response.data);
 // Clear the URL parameter after loading
 navigate('/batch-tracker', { replace: true });
 toast.success('Batch loaded from history!');
 }
 } catch (error) {
 console.error('Error loading batch from URL:', error);
 toast.error('Failed to load batch tracker from history');
 } finally {
 setLoading(false);
 }
 }
 };
 loadFromUrl();
 }, [searchParams, freezeDryerModels, API, navigate]);
 
 // Initialize trays when model is selected (but not when loading saved data)
 useEffect(() => {
 if (selectedModel) {
 // Only initialize if trays are empty OR if the number of trays doesn't match the model
 const numTrays = selectedModel.trays || 4;
 const shouldInitialize = trays.length === 0 || 
 (trays.length !== numTrays && trays.every(t => !t.product_name && !t.tray_weight_g));
 
 if (shouldInitialize) {
 const newTrays = Array.from({ length: numTrays }, (_, index) => ({
 id: `tray-${index + 1}`,
 tray_number: index + 1,
 product_name: '',
 product_category: 'fruits',
 tray_weight_g: '',
 total_wet_tray_weight_g: '',
 wet_weight_g: '',
 proposed_dry_weight_g: 0,
 completed_tray_weight_g: '',
 actual_dry_weight_g: 0
 }));
 setTrays(newTrays);
 }
 }
 }, [selectedModel]);
 
 // Weight conversion functions
 const convertToGrams = (value, unit) => {
 const val = parseFloat(value) || 0;
 switch (unit) {
 case 'lbs': return val * 453.592;
 case 'oz': return val * 28.3495;
 case 'kg': return val * 1000;
 case 'g': 
 default: return val;
 }
 };
 
 const convertFromGrams = (grams, unit) => {
 const g = parseFloat(grams) || 0;
 if (g === 0) return '';
 
 let value;
 switch (unit) {
 case 'lbs': 
 value = g / 453.592;
 return value % 1 === 0 ? value.toFixed(0) : value.toFixed(2);
 case 'oz': 
 value = g / 28.3495;
 return value % 1 === 0 ? value.toFixed(0) : value.toFixed(2);
 case 'kg': 
 value = g / 1000;
 return value % 1 === 0 ? value.toFixed(0) : value.toFixed(3);
 case 'g': 
 default: 
 return g % 1 === 0 ? g.toFixed(0) : g.toFixed(1);
 }
 };
 
 const getUnitLabel = (unit) => {
 switch (unit) {
 case 'lbs': return 'lbs';
 case 'oz': return 'oz';
 case 'kg': return 'kg';
 case 'g':
 default: return 'g';
 }
 };

 // Calculate proposed dry weight
 const calculateProposedDryWeight = (tray) => {
 if (!tray.wet_weight_g || !tray.product_category) return 0;
 
 const category = foodCategories[tray.product_category];
 if (!category) return 0;
 
 const wetWeight = parseFloat(tray.wet_weight_g) || 0;
 const moisture = category.moisture_percent / 100;
 const dryWeight = wetWeight * (1 - moisture);
 
 return dryWeight;
 };
 
 // Update tray data
 const updateTray = (trayId, field, value) => {
 setTrays(prevTrays => {
 const updated = prevTrays.map(tray => {
 if (tray.id === trayId) {
 const updatedTray = { ...tray, [field]: value };
 
 // Auto-calculate Food Wet Weight when Total Wet or Tray Weight changes
 if (field === 'total_wet_tray_weight_g' || field === 'tray_weight_g') {
 const totalWet = field === 'total_wet_tray_weight_g' ? parseFloat(value) : parseFloat(updatedTray.total_wet_tray_weight_g);
 const trayWeight = field === 'tray_weight_g' ? parseFloat(value) : parseFloat(updatedTray.tray_weight_g);
 
 if (!isNaN(totalWet) && !isNaN(trayWeight) && totalWet > 0 && trayWeight > 0 && totalWet > trayWeight) {
 updatedTray.wet_weight_g = (totalWet - trayWeight).toFixed(1);
 }
 }
 
 // Recalculate proposed dry weight when wet weight or category changes
 if (field === 'wet_weight_g' || field === 'product_category' || field === 'total_wet_tray_weight_g' || field === 'tray_weight_g') {
 updatedTray.proposed_dry_weight_g = calculateProposedDryWeight(updatedTray);
 }
 
 // Auto-calculate actual dry weight when completed tray weight or tray weight changes
 if (field === 'completed_tray_weight_g' || field === 'tray_weight_g') {
 const completedWeight = field === 'completed_tray_weight_g' ? parseFloat(value) : parseFloat(updatedTray.completed_tray_weight_g);
 const trayWeight = field === 'tray_weight_g' ? parseFloat(value) : parseFloat(updatedTray.tray_weight_g);
 
 if (!isNaN(completedWeight) && !isNaN(trayWeight) && completedWeight > 0 && trayWeight > 0) {
 updatedTray.actual_dry_weight_g = completedWeight - trayWeight;
 } else {
 updatedTray.actual_dry_weight_g = 0;
 }
 }
 
 return updatedTray;
 }
 return tray;
 });
 return updated;
 });
 };
 
 // Load from previous batch
 const loadFromPrevious = (batch) => {
 if (batch.source === 'business') {
 // Load from business calculator - single product
 const planName = batch.plan_name || batch.inputs?.plan_name || 'Unnamed';
 setBatchName(`${planName} - Tracked`);
 
 const product = batch.inputs?.product_description || 'Unknown Product';
 const items = [{
 product_name: product,
 product_category: 'fruits',
 assignedTrays: [] // Will be populated by user
 }];
 
 setItemsToAssign(items);
 setShowImportModal(false);
 
 // If model not selected, prompt user
 if (!selectedModel) {
 toast.info('Please select your freeze dryer model first');
 return;
 }
 
 // Show tray assignment modal
 setShowTrayAssignmentModal(true);
 
 } else {
 // Load from household calculator - multiple items
 setBatchName(`${batch.inputs.batch_name} - Tracked`);
 
 const model = freezeDryerModels.find(m => m.id === batch.inputs.freeze_dryer_model_id);
 if (model) {
 setSelectedModel(model);
 
 // Prepare items for assignment - only product info, no weights
 const items = (batch.inputs.items || []).map(item => ({
 product_name: item.food_name,
 product_category: item.category,
 assignedTrays: [] // Will be populated by user
 }));
 
 setItemsToAssign(items);
 setShowImportModal(false);
 
 // Show tray assignment modal
 setTimeout(() => {
 setShowTrayAssignmentModal(true);
 }, 100);
 }
 }
 };
 
 // Apply tray assignments
 const applyTrayAssignments = () => {
 if (!selectedModel) {
 toast.error('Please select a freeze dryer model first');
 return;
 }
 
 // Create trays based on assignments - only product info, weights to be entered
 const newTrays = Array.from({ length: selectedModel.trays }, (_, index) => {
 const trayNumber = index + 1;
 
 // Find which item is assigned to this tray
 const assignedItem = itemsToAssign.find(item => 
 item.assignedTrays.includes(trayNumber)
 );
 
 if (assignedItem) {
 return {
 id: `tray-${trayNumber}`,
 tray_number: trayNumber,
 product_name: assignedItem.product_name,
 product_category: assignedItem.product_category,
 tray_weight_g: '',
 total_wet_tray_weight_g: '',
 wet_weight_g: '',
 proposed_dry_weight_g: 0,
 completed_tray_weight_g: '',
 actual_dry_weight_g: 0
 };
 }
 
 // Empty tray
 return {
 id: `tray-${trayNumber}`,
 tray_number: trayNumber,
 product_name: '',
 product_category: 'fruits',
 tray_weight_g: '',
 total_wet_tray_weight_g: '',
 wet_weight_g: '',
 proposed_dry_weight_g: 0,
 completed_tray_weight_g: '',
 actual_dry_weight_g: 0
 };
 });
 
 setTrays(newTrays);
 setShowTrayAssignmentModal(false);
 toast.success('Products assigned! Now weigh your trays and enter the measurements.');
 };
 
 // Toggle tray assignment for an item
 const toggleTrayAssignment = (itemIndex, trayNumber) => {
 setItemsToAssign(prev => {
 const updated = [...prev];
 const item = updated[itemIndex];
 
 if (item.assignedTrays.includes(trayNumber)) {
 // Remove tray
 item.assignedTrays = item.assignedTrays.filter(t => t !== trayNumber);
 } else {
 // Add tray
 item.assignedTrays = [...item.assignedTrays, trayNumber].sort((a, b) => a - b);
 }
 
 return updated;
 });
 };
 
 // Save batch tracker
 const saveBatchTracker = async () => {
 if (!batchName) {
 toast.error('Please enter a batch name');
 return;
 }
 
 if (!selectedModel) {
 toast.error('Please select a freeze dryer model');
 return;
 }
 
 setLoading(true);
 try {
 // Convert trays to ensure all numeric fields are strings
 const traysToSave = trays.map(tray => ({
 ...tray,
 actual_dry_weight_g: tray.actual_dry_weight_g.toString()
 }));
 
 await axios.post(`${API}/batch-tracker/save`, {
 batch_name: batchName,
 freeze_dryer_model_id: selectedModel.id,
 unit: weightUnit,
 trays: traysToSave
 });
 
 toast.success('Batch tracker saved successfully!');
 
 // Refresh saved trackers list
 const response = await axios.get(`${API}/batch-tracker/logs`);
 setSavedBatchTrackers(response.data.logs || []);
 } catch (error) {
 console.error('Save error:', error);
 toast.error('Failed to save batch tracker');
 } finally {
 setLoading(false);
 }
 };
 
 // Load saved batch tracker
 const loadSavedTracker = (tracker) => {
 setBatchName(tracker.batch_name);
 
 // Load weight unit if available, default to 'g'
 if (tracker.unit) {
 setWeightUnit(tracker.unit);
 }
 
 const model = freezeDryerModels.find(m => m.id === tracker.freeze_dryer_model_id);
 if (model) {
 // IMPORTANT: Set trays FIRST, then model
 // This prevents the model's useEffect from overwriting the tray data
 setTrays(tracker.trays);
 setSelectedModel(model);
 toast.success('Batch tracker loaded successfully!');
 } else {
 toast.error('Machine model not found');
 }
 };
 
 // Print batch tracker
 const printBatchTracker = () => {
 window.print();
 };
 
 // Check if tray passes dryness test
 const checkTrayDryness = (tray) => {
 if (!tray.actual_dry_weight_g || !tray.proposed_dry_weight_g || tray.proposed_dry_weight_g === 0) {
 return null; // Not enough data to check
 }
 
 const actualDry = parseFloat(tray.actual_dry_weight_g) || 0;
 const proposedDry = parseFloat(tray.proposed_dry_weight_g) || 0;
 
 return {
 passed: actualDry <= proposedDry,
 actualDry,
 proposedDry,
 difference: actualDry - proposedDry,
 percentageOfProposed: proposedDry > 0 ? ((actualDry / proposedDry) * 100).toFixed(1) : 0
 };
 };
 
 // Check machine capacity
 const checkMachineCapacity = () => {
 if (!selectedModel) return null;
 
 // Sum all food wet weights
 const totalFoodWeight = trays.reduce((sum, tray) => {
 return sum + (parseFloat(tray.wet_weight_g) || 0);
 }, 0);
 
 const maxCapacityG = selectedModel.max_batch_weight_lb * 453.592;
 const totalLbs = totalFoodWeight / 453.592;
 const maxLbs = selectedModel.max_batch_weight_lb;
 const percentageUsed = (totalLbs / maxLbs) * 100;
 
 return {
 isOverfilled: totalLbs > maxLbs,
 totalWeightG: totalFoodWeight,
 totalWeightLbs: totalLbs,
 maxWeightLbs: maxLbs,
 percentageUsed: percentageUsed.toFixed(1),
 excessWeightLbs: totalLbs > maxLbs ? (totalLbs - maxLbs).toFixed(2) : 0
 };
 };

 // Calculate totals
 const totals = {
 wet_weight: trays.reduce((sum, tray) => sum + (parseFloat(tray.wet_weight_g) || 0), 0),
 proposed_dry: trays.reduce((sum, tray) => sum + (parseFloat(tray.proposed_dry_weight_g) || 0), 0),
 actual_dry: trays.reduce((sum, tray) => sum + (parseFloat(tray.actual_dry_weight_g) || 0), 0)
 };
 
 const actualVsProposed = totals.actual_dry > 0 
 ? ((totals.actual_dry / totals.proposed_dry) * 100).toFixed(1)
 : 0;

 return (
 <div className="min-h-screen bg-background dark:from-neutral-900 dark:to-neutral-800">
 <ToolSelector />
 <PageHero
 eyebrow="Live batch"
 eyebrowIcon={ClipboardList}
 title={<>Batch <span className="text-amber-brand">Tracker</span></>}
 description="Log per-tray progress through a live freeze-dry cycle so you can compare runs later."
 backTo="/calculators"
 backLabel="Calculators"
 testId="batch-tracker-hero"
 />
 <div className="container mx-auto px-4 py-8 max-w-7xl">
 {/* Tutorial */}
 <CalculatorTutorial
 storageKey="batch-tracker-v1"
 title="How to run a tracked batch"
 steps={[
 { title: '1 · Start a new batch', body: 'Give the batch a name and pick your freeze dryer model. Tray count comes from the model automatically.' },
 { title: '2 · Assign items to trays', body: 'Add each item and tap the tray buttons to assign it to one or more trays. You can split large items across multiple trays.' },
 { title: '3 · Log each phase', body: 'As the machine progresses through freezing → primary → secondary drying, log timestamps and observations per tray so you can debug bad runs later.' },
 { title: '4 · Finish + review', body: 'Mark the batch complete when you package. All timings + notes stay under Batch Logs so you can compare runs over time.' },
 ]}
 />

 {/* Compact liability disclaimer */}
 <Card className="border border-primary/40 bg-primary/5 mb-6 no-print">
 <CardContent className="pt-4">
 <div className="flex gap-3 items-start">
 <ShieldCheck className="w-4 h-4 text-primary flex-shrink-0 mt-1" />
 <p className="text-sm text-foreground/80 leading-relaxed">
 <strong className="text-foreground">Educational tool.</strong> Freeze Dry Academy is not
 liable for the outcome of any batch. You&apos;re responsible for equipment operation and
 food-safety practices.
 </p>
 </div>
 </CardContent>
 </Card>

 {/* Family B — headline metric snapshot of the live batch */}
 <div className="mb-6" data-testid="batch-family-b-metric">
 <HeadlineMetric
 label={batchName || 'Live batch tracker'}
 value={totals.actual_dry > 0 ? `${actualVsProposed}%` : `${trays.length}`}
 valueSuffix={totals.actual_dry > 0 ? 'dryness vs. target' : 'trays queued'}
 hint={totals.actual_dry > 0
 ? 'Actual dry weight as a share of your proposed target across all trays.'
 : 'Add your trays and log wet weights to start tracking. Actual dryness lights up once you record dry weights.'}
 sideMetrics={[
 { label: 'Trays', value: `${trays.length}` },
 { label: 'Wet total', value: `${(totals.wet_weight / 453.592).toFixed(2)} lb` },
 { label: 'Model', value: selectedModel?.display_name?.split(' ')[0] || '—' },
 ]}
 testId="batch-headline-metric"
 />
 </div>

 {/* Advertisement Banner - Header */}
 <div className="no-print mb-6 p-4 bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 rounded-lg border-2 border-amber-300 dark:border-amber-700 shadow-sm">
 <div className="flex items-center justify-between gap-4">
 <div className="flex items-center gap-3">
 <div className="w-12 h-12 bg-gradient-to-br from-amber-500 to-orange-500 rounded-lg flex items-center justify-center flex-shrink-0">
 <Sparkles className="w-6 h-6 text-white" />
 </div>
 <div>
 <p className="text-sm font-bold text-amber-900 dark:text-amber-100">
 🎓 Master Freeze-Drying with Our Online Courses!
 </p>
 <p className="text-xs text-amber-700 dark:text-amber-300">
 Learn from experts • Step-by-step tutorials • Community support
 </p>
 </div>
 </div>
 <Button
 size="sm"
 className="bg-amber-600 hover:bg-amber-700 text-white whitespace-nowrap"
 >
 Learn More
 </Button>
 </div>
 </div>

 {/* Batch Name & Actions */}
 <Card className="mb-6">
 <CardHeader>
 <CardTitle>Batch Information</CardTitle>
 <CardDescription>Name your batch and select your machine</CardDescription>
 </CardHeader>
 <CardContent>
 <div className="grid md:grid-cols-3 gap-4">
 <div className="space-y-2">
 <Label>Batch Name</Label>
 <Input
 value={batchName}
 onChange={(e) => setBatchName(e.target.value)}
 placeholder="e.g., Summer Harvest 2024"
 className="text-lg"
 />
 </div>
 
 <div className="space-y-2">
 <Label>Weight Unit</Label>
 <select
 value={weightUnit}
 onChange={(e) => setWeightUnit(e.target.value)}
 className="w-full px-3 py-2 text-lg border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-800 font-semibold"
 >
 <option value="g">Grams (g)</option>
 <option value="lbs">Pounds (lbs)</option>
 <option value="oz">Ounces (oz)</option>
 <option value="kg">Kilograms (kg)</option>
 </select>
 </div>
 
 <div className="space-y-3">
 <Label className="text-base font-semibold">Quick Actions</Label>
 <div className="flex flex-wrap gap-3">
 <Button
 onClick={() => setShowLoadPreviousModal(true)}
 variant="outline"
 size="lg"
 className="flex-1 min-w-[180px] border-2 border-blue-500 text-blue-700 hover:bg-blue-50 hover:border-blue-600 transition-all"
 >
 <Package className="w-5 h-5 mr-2" />
 Load Previous
 </Button>
 <Button
 onClick={() => setShowImportModal(true)}
 variant="outline"
 size="lg"
 className="flex-1 min-w-[180px] border-2 border-green-500 text-green-700 hover:bg-green-50 hover:border-green-600 transition-all"
 >
 <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
 </svg>
 Import Record
 </Button>
 <Button
 onClick={() => navigate('/batch-history')}
 variant="outline"
 size="lg"
 className="flex-1 min-w-[180px] border-2 border-primary/30 text-primary hover:bg-primary/5 hover:border-primary/30 transition-all"
 >
 <ClipboardList className="w-5 h-5 mr-2" />
 View History
 </Button>
 </div>
 </div>
 </div>
 </CardContent>
 </Card>

 {/* Machine Selection */}
 <Card className="mb-6">
 <CardHeader>
 <CardTitle>Select Your Freeze Dryer</CardTitle>
 <CardDescription>This determines the number of trays to track</CardDescription>
 </CardHeader>
 <CardContent>
 <div className="grid md:grid-cols-2 gap-3">
 {freezeDryerModels.map(model => (
 <label
 key={model.id}
 className={`flex items-start gap-3 p-3 border-2 rounded-lg cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800 transition-all ${
 selectedModel?.id === model.id
 ? 'border-green-500 bg-green-50 dark:bg-green-900/30 shadow-md'
 : 'border-slate-200 dark:border-slate-700'
 }`}
 >
 <input
 type="radio"
 name="freeze-dryer"
 value={model.id}
 checked={selectedModel?.id === model.id}
 onChange={() => setSelectedModel(model)}
 className="w-4 h-4 mt-1"
 />
 <div className="flex-1">
 <span className="text-sm font-medium block">{model.display_name}</span>
 <span className="text-xs text-muted-foreground block mt-1">
 {model.trays} trays • Max: {model.max_batch_weight_lb} lbs
 </span>
 </div>
 </label>
 ))}
 </div>
 </CardContent>
 </Card>

 {/* Tray Tracking Grid */}
 {selectedModel && trays.length > 0 && (
 <>
 <Card className="mb-6">
 <CardHeader>
 <CardTitle>Tray Tracking - {selectedModel.display_name}</CardTitle>
 <CardDescription>
 Track each tray individually. Proposed dry weight is calculated automatically based on product type.
 </CardDescription>
 </CardHeader>
 <CardContent>
 {/* Weighing Reminder */}
 <div className="mb-6 p-4 bg-pro-soft dark:from-blue-900/20 dark:to-cyan-900/20 rounded-lg border-2 border-blue-300 dark:border-blue-700">
 <div className="flex items-start gap-3">
 <Scale className="w-6 h-6 text-blue-600 dark:text-blue-400 flex-shrink-0" />
 <div className="flex-1">
 <h4 className="font-bold text-blue-900 dark:text-blue-100 mb-2">
 ⚖️ Weighing Instructions
 </h4>
 <div className="grid md:grid-cols-2 gap-3 text-sm text-blue-800 dark:text-blue-200">
 <div>
 <p className="font-semibold mb-1">📏 Before Drying:</p>
 <ol className="list-decimal list-inside space-y-1 text-xs">
 <li>Weigh empty tray → <strong>Empty Tray Weight</strong></li>
 <li>Load food onto tray</li>
 <li>Weigh tray + food → <strong>Total Wet Tray Weight</strong></li>
 <li>Food weight calculates automatically!</li>
 </ol>
 </div>
 <div>
 <p className="font-semibold mb-1">✅ After Drying:</p>
 <ol className="list-decimal list-inside space-y-1 text-xs">
 <li>Wait for cycle to complete</li>
 <li>Weigh tray + dried food → <strong>Completed Tray Weight</strong></li>
 <li>System validates if fully dried</li>
 </ol>
 </div>
 </div>
 </div>
 </div>
 </div>
 <div className="space-y-4">
 {trays.map((tray, index) => (
 <div 
 key={tray.id}
 className="p-4 border-2 border-slate-200 dark:border-slate-700 rounded-lg bg-slate-50 dark:bg-slate-800"
 >
 <div className="flex items-center justify-between mb-3">
 <h3 className="font-bold text-lg text-green-700 dark:text-green-400">
 Tray {tray.tray_number}
 </h3>
 <span className="text-xs text-muted-foreground">
 {tray.product_name || 'Empty'}
 </span>
 </div>

 <div className="space-y-3">
 {/* Row 1: Product Info */}
 <div className="grid md:grid-cols-2 gap-3">
 {/* Product Name */}
 <div className="space-y-2">
 <Label className="text-xs">Product Name</Label>
 <Input
 value={tray.product_name}
 onChange={(e) => updateTray(tray.id, 'product_name', e.target.value)}
 placeholder="e.g., Strawberries"
 className="text-sm"
 />
 </div>

 {/* Product Category */}
 <div className="space-y-2">
 <Label className="text-xs">Product Category</Label>
 <select
 value={tray.product_category}
 onChange={(e) => updateTray(tray.id, 'product_category', e.target.value)}
 className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-800"
 >
 {Object.entries(foodCategories).map(([key, cat]) => (
 <option key={key} value={key}>
 {cat.name} ({cat.moisture_percent}% moisture)
 </option>
 ))}
 </select>
 </div>
 </div>

 {/* Row 2: Before Drying Measurements */}
 <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-300 dark:border-blue-700">
 <Label className="text-xs font-bold text-blue-800 dark:text-blue-200 mb-2 block">
 ⚖️ Before Drying
 </Label>
 <div className="grid md:grid-cols-3 gap-3">
 {/* Tray Weight */}
 <div className="space-y-2">
 <Label className="text-xs">Empty Tray Weight ({getUnitLabel(weightUnit)})</Label>
 <Input
 type="number"
 step="any"
 value={tray.tray_weight_g ? convertFromGrams(tray.tray_weight_g, weightUnit) : ''}
 onChange={(e) => {
 const inputValue = e.target.value;
 if (inputValue === '' || inputValue === '-') {
 updateTray(tray.id, 'tray_weight_g', '');
 return;
 }
 const gramsValue = convertToGrams(inputValue, weightUnit);
 updateTray(tray.id, 'tray_weight_g', gramsValue.toString());
 }}
 placeholder="0"
 className="text-sm"
 />
 </div>

 {/* Total Wet Tray Weight */}
 <div className="space-y-2">
 <Label className="text-xs">Total Wet Tray Weight ({getUnitLabel(weightUnit)})</Label>
 <Input
 type="number"
 step="any"
 value={tray.total_wet_tray_weight_g ? convertFromGrams(tray.total_wet_tray_weight_g, weightUnit) : ''}
 onChange={(e) => {
 const inputValue = e.target.value;
 if (inputValue === '' || inputValue === '-') {
 updateTray(tray.id, 'total_wet_tray_weight_g', '');
 return;
 }
 const gramsValue = convertToGrams(inputValue, weightUnit);
 updateTray(tray.id, 'total_wet_tray_weight_g', gramsValue.toString());
 }}
 placeholder="Tray + wet food"
 className="text-sm"
 />
 <p className="text-xs text-muted-foreground">Weigh tray with food before drying</p>
 </div>

 {/* Wet Weight (auto-calculated) */}
 <div className="space-y-2">
 <Label className="text-xs font-bold text-blue-700 dark:text-blue-300">Food Wet Weight ({getUnitLabel(weightUnit)})</Label>
 <div className="px-3 py-2 text-sm font-bold bg-blue-100 dark:bg-blue-900/30 border-2 border-blue-400 dark:border-blue-600 rounded-md">
 {tray.wet_weight_g ? convertFromGrams(tray.wet_weight_g, weightUnit) : '0.0'}
 </div>
 <p className="text-xs text-blue-600 dark:text-blue-400">Auto-calculated</p>
 </div>
 </div>
 </div>

 {/* Row 3: After Drying Measurements */}
 <div className="p-3 bg-primary/5 dark:bg-navy/20 rounded-lg border border-primary/20">
 <Label className="text-xs font-bold text-primary dark:text-primary mb-2 block">
 ✅ After Drying
 </Label>
 <div className="grid md:grid-cols-3 gap-3">
 {/* Proposed Dry Weight */}
 <div className="space-y-2">
 <Label className="text-xs text-green-700 dark:text-green-400">
 Proposed Dry Weight ({getUnitLabel(weightUnit)})
 </Label>
 <div className="px-3 py-2 text-sm font-bold bg-green-50 dark:bg-green-900/20 border border-green-300 dark:border-green-700 rounded-md">
 {convertFromGrams(tray.proposed_dry_weight_g, weightUnit)}
 </div>
 <p className="text-xs text-green-700 dark:text-green-400">Target weight</p>
 </div>

 {/* Completed Tray Weight */}
 <div className="space-y-2">
 <Label className="text-xs font-bold">Completed Tray Weight ({getUnitLabel(weightUnit)}) *</Label>
 <Input
 type="number"
 step="any"
 value={tray.completed_tray_weight_g ? convertFromGrams(tray.completed_tray_weight_g, weightUnit) : ''}
 onChange={(e) => {
 const inputValue = e.target.value;
 if (inputValue === '' || inputValue === '-') {
 updateTray(tray.id, 'completed_tray_weight_g', '');
 return;
 }
 const gramsValue = convertToGrams(inputValue, weightUnit);
 updateTray(tray.id, 'completed_tray_weight_g', gramsValue.toString());
 }}
 placeholder="Tray + dry food"
 className="text-sm font-semibold border-primary/30"
 />
 <p className="text-xs text-muted-foreground">Weigh tray with dried food</p>
 </div>

 {/* Actual Dry Weight (auto-calculated) */}
 <div className="space-y-2">
 <Label className="text-xs font-bold text-primary dark:text-primary">
 Actual Dry Weight ({getUnitLabel(weightUnit)})
 </Label>
 <div className="px-3 py-2 text-sm font-bold bg-primary/5 dark:bg-navy/30 border-2 border-primary/30 dark:border-primary/30 rounded-md">
 {convertFromGrams(tray.actual_dry_weight_g, weightUnit)}
 </div>
 <p className="text-xs text-primary dark:text-primary">Auto-calculated</p>
 </div>
 </div>
 </div>
 </div>

 {/* Tray Analysis - Pass/Fail Validation */}
 {(() => {
 const drynessCheck = checkTrayDryness(tray);
 if (!drynessCheck) return null;
 
 return (
 <div className={`mt-3 p-4 rounded-lg border-2 ${
 drynessCheck.passed
 ? 'bg-green-50 dark:bg-green-900/20 border-green-400 dark:border-green-600'
 : 'bg-red-50 dark:bg-red-900/20 border-red-400 dark:border-red-600'
 }`}>
 <div className="flex items-start gap-3">
 {drynessCheck.passed ? (
 <CheckCircle className="w-6 h-6 text-green-600 dark:text-green-400 flex-shrink-0" />
 ) : (
 <AlertCircle className="w-6 h-6 text-red-600 dark:text-red-400 flex-shrink-0 animate-pulse" />
 )}
 <div className="flex-1">
 <p className={`text-sm font-bold ${
 drynessCheck.passed
 ? 'text-green-800 dark:text-green-200'
 : 'text-red-800 dark:text-red-200'
 }`}>
 {drynessCheck.passed 
 ? '✅ PASS - Tray is Fully Dried' 
 : '❌ FAIL - Tray Not Completely Dry'}
 </p>
 <div className="mt-2 space-y-1 text-xs">
 <div className="flex justify-between">
 <span className="text-muted-foreground">Actual Dry Weight:</span>
 <span className="font-bold">{convertFromGrams(drynessCheck.actualDry, weightUnit)} {getUnitLabel(weightUnit)}</span>
 </div>
 <div className="flex justify-between">
 <span className="text-muted-foreground">Proposed Dry Weight:</span>
 <span className="font-bold">{convertFromGrams(drynessCheck.proposedDry, weightUnit)} {getUnitLabel(weightUnit)}</span>
 </div>
 <div className="flex justify-between">
 <span className="text-muted-foreground">Difference:</span>
 <span className={`font-bold ${
 drynessCheck.difference > 0 ? 'text-red-600' : 'text-green-600'
 }`}>
 {drynessCheck.difference > 0 ? '+' : ''}{convertFromGrams(Math.abs(drynessCheck.difference), weightUnit)} {getUnitLabel(weightUnit)}
 </span>
 </div>
 <div className="flex justify-between pt-1 border-t border-slate-300 dark:border-slate-600">
 <span className="text-muted-foreground">Percentage of Target:</span>
 <span className="font-bold">{drynessCheck.percentageOfProposed}%</span>
 </div>
 </div>
 {!drynessCheck.passed && (
 <div className="mt-3 p-2 bg-red-100 dark:bg-red-900/30 rounded">
 <p className="text-xs font-semibold text-red-800 dark:text-red-200">
 ⚠️ This tray still contains moisture. The actual weight is {convertFromGrams(drynessCheck.difference, weightUnit)} {getUnitLabel(weightUnit)} heavier than expected. 
 Continue drying or check your measurements.
 </p>
 </div>
 )}
 </div>
 </div>
 </div>
 );
 })()}
 </div>
 ))}
 </div>
 </CardContent>
 </Card>

 {/* Machine Capacity Warning */}
 {(() => {
 const capacityCheck = checkMachineCapacity();
 if (!capacityCheck || capacityCheck.totalWeightLbs === 0) return null;
 
 return (
 <Card className={`mb-6 border-2 ${
 capacityCheck.isOverfilled 
 ? 'border-red-500 dark:border-red-700 bg-red-50 dark:bg-red-900/20' 
 : 'border-blue-300 dark:border-blue-700'
 }`}>
 <CardHeader>
 <CardTitle className={`text-xl flex items-center gap-2 ${
 capacityCheck.isOverfilled ? 'text-red-900 dark:text-red-100' : 'text-blue-900 dark:text-blue-100'
 }`}>
 <Scale className="w-5 h-5" />
 Machine Weight Capacity
 </CardTitle>
 </CardHeader>
 <CardContent>
 <div className="space-y-4">
 {/* Capacity Bar */}
 <div>
 <div className="flex justify-between text-sm mb-2">
 <span className="font-semibold">Total Food Weight</span>
 <span className="font-bold text-lg">
 {capacityCheck.totalWeightLbs.toFixed(2)} lbs / {capacityCheck.maxWeightLbs} lbs
 </span>
 </div>
 <div className="w-full bg-slate-200 dark:bg-slate-700 rounded-full h-6 overflow-hidden shadow-inner">
 <div
 className={`h-full rounded-full transition-all duration-300 ${
 capacityCheck.isOverfilled
 ? 'bg-red-600 dark:bg-red-500'
 : capacityCheck.percentageUsed > 90
 ? 'bg-amber-500 dark:bg-amber-400'
 : 'bg-green-600 dark:bg-green-500'
 }`}
 style={{ width: `${Math.min(capacityCheck.percentageUsed, 100)}%` }}
 />
 </div>
 <p className="text-xs text-muted-foreground mt-1 text-center">
 {capacityCheck.percentageUsed}% of machine capacity
 </p>
 </div>

 {/* Overfill Error */}
 {capacityCheck.isOverfilled && (
 <div className="p-4 bg-red-100 dark:bg-red-900/30 rounded-lg border-2 border-red-500 dark:border-red-700">
 <div className="flex items-start gap-3">
 <AlertCircle className="w-8 h-8 text-red-600 dark:text-red-400 flex-shrink-0 animate-pulse" />
 <div>
 <h4 className="text-base font-bold text-red-900 dark:text-red-100 mb-2">
 ⚠️ MACHINE OVERFILL ERROR
 </h4>
 <div className="space-y-2 text-sm text-red-800 dark:text-red-200">
 <p>
 <strong>Excess weight: {capacityCheck.excessWeightLbs} lbs</strong>
 </p>
 <p>
 You have exceeded the {selectedModel.display_name} maximum capacity of {capacityCheck.maxWeightLbs} lbs by {capacityCheck.excessWeightLbs} lbs.
 </p>
 <div className="mt-3 p-3 bg-red-200 dark:bg-red-900/50 rounded border border-red-600 dark:border-red-800">
 <p className="font-bold text-red-900 dark:text-red-100 mb-2">⚠️ CRITICAL WARNINGS:</p>
 <ul className="list-disc list-inside space-y-1 text-xs">
 <li>Cycle will take significantly longer to complete</li>
 <li>May result in incomplete drying (food spoilage risk)</li>
 <li>Increased wear on vacuum pump and heating elements</li>
 <li><strong className="text-red-950 dark:text-red-50">MAY VOID MANUFACTURER WARRANTY</strong></li>
 </ul>
 </div>
 <p className="mt-3 font-semibold">
 ✅ <strong>Action Required:</strong> Remove at least {capacityCheck.excessWeightLbs} lbs of food before starting the freeze dryer.
 </p>
 </div>
 </div>
 </div>
 </div>
 )}

 {/* Near Capacity Warning */}
 {!capacityCheck.isOverfilled && capacityCheck.percentageUsed > 90 && (
 <div className="p-3 bg-amber-50 dark:bg-amber-900/20 rounded border border-amber-400 dark:border-amber-700">
 <div className="flex items-start gap-2">
 <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0" />
 <div>
 <p className="text-sm text-amber-800 dark:text-amber-200">
 <strong>Near Maximum Capacity</strong> - You're using {capacityCheck.percentageUsed}% of your machine's capacity. 
 Consider removing some food for optimal performance.
 </p>
 </div>
 </div>
 </div>
 )}
 </div>
 </CardContent>
 </Card>
 );
 })()}

 {/* Summary Card */}
 <Card className="mb-6 border-2 border-green-300 dark:border-green-700">
 <CardHeader>
 <CardTitle className="text-2xl text-green-900 dark:text-green-100">
 Batch Summary
 </CardTitle>
 </CardHeader>
 <CardContent>
 <div className="grid md:grid-cols-3 gap-4">
 <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
 <div className="flex items-center gap-2 mb-2">
 <Scale className="w-5 h-5 text-blue-600" />
 <span className="text-sm font-medium text-blue-800 dark:text-blue-200">
 Total Wet Weight
 </span>
 </div>
 <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">
 {convertFromGrams(totals.wet_weight, weightUnit) || '0'} {getUnitLabel(weightUnit)}
 </p>
 {weightUnit !== 'lbs' && (
 <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
 {convertFromGrams(totals.wet_weight, 'lbs')} lbs
 </p>
 )}
 </div>

 <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
 <div className="flex items-center gap-2 mb-2">
 <TrendingUp className="w-5 h-5 text-green-600" />
 <span className="text-sm font-medium text-green-800 dark:text-green-200">
 Proposed Dry Weight
 </span>
 </div>
 <p className="text-2xl font-bold text-green-600 dark:text-green-400">
 {convertFromGrams(totals.proposed_dry, weightUnit) || '0'} {getUnitLabel(weightUnit)}
 </p>
 {weightUnit !== 'lbs' && (
 <p className="text-xs text-green-600 dark:text-green-400 mt-1">
 {convertFromGrams(totals.proposed_dry, 'lbs')} lbs
 </p>
 )}
 </div>

 <div className="p-4 bg-primary/5 dark:bg-navy/20 rounded-lg">
 <div className="flex items-center gap-2 mb-2">
 <CheckCircle className="w-5 h-5 text-primary" />
 <span className="text-sm font-medium text-primary dark:text-primary">
 Actual Dry Weight
 </span>
 </div>
 <p className="text-2xl font-bold text-primary dark:text-primary">
 {convertFromGrams(totals.actual_dry, weightUnit) || '0'} {getUnitLabel(weightUnit)}
 </p>
 {totals.actual_dry > 0 && (
 <p className="text-xs text-primary dark:text-primary mt-1">
 {actualVsProposed}% of proposed
 </p>
 )}
 </div>
 </div>

 {/* Performance Indicator */}
 {totals.actual_dry > 0 && (
 <div className="mt-4 p-4 bg-slate-100 dark:bg-slate-800 rounded-lg">
 <div className="flex items-start gap-3">
 {actualVsProposed >= 90 && actualVsProposed <= 110 ? (
 <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0" />
 ) : (
 <AlertCircle className="w-6 h-6 text-amber-600 flex-shrink-0" />
 )}
 <div>
 <p className="font-semibold text-sm">
 {actualVsProposed >= 90 && actualVsProposed <= 110 
 ? '✅ Excellent Performance!' 
 : '⚠️ Results Outside Expected Range'}
 </p>
 <p className="text-xs text-muted-foreground mt-1">
 {actualVsProposed >= 90 && actualVsProposed <= 110
 ? 'Your actual dry weight is very close to the proposed weight. Great job!'
 : actualVsProposed < 90
 ? 'Actual weight is lower than expected. Food may be over-dried or there was more moisture than estimated.'
 : 'Actual weight is higher than expected. Food may not be fully dried yet.'}
 </p>
 </div>
 </div>
 </div>
 )}
 </CardContent>
 </Card>

 {/* Action Buttons */}
 <div className="grid md:grid-cols-2 gap-3">
 <Button
 onClick={saveBatchTracker}
 disabled={loading || !batchName}
 className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-lg py-6"
 >
 {loading ? (
 <>
 <Loader className="w-5 h-5 mr-2 animate-spin" />
 Saving...
 </>
 ) : (
 <>
 <Save className="w-5 h-5 mr-2" />
 Save Batch
 </>
 )}
 </Button>
 
 <Button
 onClick={printBatchTracker}
 variant="outline"
 className="border-slate-500 text-slate-700 hover:bg-slate-50 text-lg py-6"
 >
 <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
 </svg>
 Print Batch
 </Button>
 </div>
 </>
 )}

 {/* Print Styles */}
 {/* Advertisement Banner - Footer */}
 <div className="no-print mt-8 p-6 bg-pro-soft rounded-lg border-2 border-blue-300 dark:border-blue-700 shadow-lg">
 <div className="text-center mb-4">
 <h3 className="text-xl font-bold text-blue-900 dark:text-blue-100 mb-2">
 🌟 Join the Freeze Dry Academy Community
 </h3>
 <p className="text-sm text-blue-700 dark:text-blue-300">
 Connect with thousands of freeze-drying enthusiasts, share recipes, and get expert advice!
 </p>
 </div>
 <div className="grid md:grid-cols-3 gap-4">
 <div className="p-4 bg-white dark:bg-slate-800 rounded-lg shadow-sm">
 <div className="text-center">
 <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center mx-auto mb-2">
 <span className="text-xl">📚</span>
 </div>
 <p className="font-semibold text-sm text-slate-900 dark:text-white">Premium Recipes</p>
 <p className="text-xs text-slate-600 dark:text-slate-400 mt-1">Curated batch recipes</p>
 </div>
 </div>
 <div className="p-4 bg-white dark:bg-slate-800 rounded-lg shadow-sm">
 <div className="text-center">
 <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center mx-auto mb-2">
 <span className="text-xl">👥</span>
 </div>
 <p className="font-semibold text-sm text-slate-900 dark:text-white">Expert Community</p>
 <p className="text-xs text-slate-600 dark:text-slate-400 mt-1">Answers from real operators</p>
 </div>
 </div>
 <div className="p-4 bg-white dark:bg-slate-800 rounded-lg shadow-sm">
 <div className="text-center">
 <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center mx-auto mb-2">
 <span className="text-xl">🎁</span>
 </div>
 <p className="font-semibold text-sm text-slate-900 dark:text-white">Exclusive Deals</p>
 <p className="text-xs text-slate-600 dark:text-slate-400 mt-1">Vendor + partner perks</p>
 </div>
 </div>
 </div>
 <div className="mt-4 text-center">
 <Button className="bg-blue-600 hover:bg-blue-700 text-white">
 Join Community
 </Button>
 </div>
 </div>

 <style>{`
 @media print {
 /* Hide non-essential elements */
 button, .no-print {
 display: none !important;
 }
 
 /* Show everything in print */
 body {
 background: white !important;
 }
 
 /* Ensure good page breaks */
 .print-section {
 page-break-inside: avoid;
 }
 
 /* Make text readable */
 * {
 color: black !important;
 background: white !important;
 }
 
 /* Keep borders visible */
 .border, .border-2 {
 border-color: #ccc !important;
 }
 }
 `}</style>

 {/* Load Previous Batch Tracker Modal */}
 {showLoadPreviousModal && (
 <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
 <div className="bg-white dark:bg-slate-900 rounded-lg max-w-2xl w-full p-6 shadow-2xl max-h-[80vh] overflow-y-auto">
 <div className="flex items-center justify-between mb-4">
 <div>
 <h3 className="text-xl font-bold">Load Previous Batch Tracker</h3>
 <p className="text-sm text-muted-foreground mt-1">
 Load a previously saved batch tracker with all measurements
 </p>
 </div>
 <Button
 variant="ghost"
 size="sm"
 onClick={() => setShowLoadPreviousModal(false)}
 >
 ✕
 </Button>
 </div>

 {savedBatchTrackers.length === 0 ? (
 <p className="text-center py-8 text-muted-foreground">
 No saved batch trackers found. Save your current batch to load it later.
 </p>
 ) : (
 <div className="space-y-2">
 {savedBatchTrackers.map((tracker, index) => (
 <button
 key={index}
 onClick={() => {
 loadSavedTracker(tracker);
 setShowLoadPreviousModal(false);
 }}
 className="w-full p-4 text-left border-2 border-slate-200 dark:border-slate-700 rounded-lg hover:border-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-all"
 >
 <div className="flex items-center justify-between">
 <div>
 <p className="font-semibold text-lg">{tracker.batch_name}</p>
 <p className="text-sm text-muted-foreground mt-1">
 {freezeDryerModels.find(m => m.id === tracker.freeze_dryer_model_id)?.display_name || 'Unknown Model'}
 {' • '}
 {tracker.trays?.length || 0} trays
 </p>
 </div>
 <div className="text-right text-xs text-muted-foreground">
 {new Date(tracker.created_at).toLocaleDateString()}
 </div>
 </div>
 </button>
 ))}
 </div>
 )}
 </div>
 </div>
 )}

 {/* Import Record from Other Calculators Modal */}
 {showImportModal && (
 <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
 <div className="bg-white dark:bg-slate-900 rounded-lg max-w-2xl w-full p-6 shadow-2xl max-h-[80vh] overflow-y-auto">
 <div className="flex items-center justify-between mb-4">
 <div>
 <h3 className="text-xl font-bold">Import Record from Other Calculators</h3>
 <p className="text-sm text-muted-foreground mt-1">
 Import product info from Business or Household Calculator batches
 </p>
 </div>
 <Button
 variant="ghost"
 size="sm"
 onClick={() => setShowImportModal(false)}
 >
 ✕
 </Button>
 </div>

 {previousBatches.length === 0 ? (
 <p className="text-center py-8 text-muted-foreground">
 No previous batches found. Create a batch in the Business or Household Calculator first.
 </p>
 ) : (
 <div className="space-y-2">
 {previousBatches.map((batch, index) => (
 <button
 key={index}
 onClick={() => loadFromPrevious(batch)}
 className="w-full p-3 text-left border-2 border-slate-200 dark:border-slate-700 rounded-lg hover:border-green-500 hover:bg-green-50 dark:hover:bg-green-900/20 transition-all"
 >
 <p className="font-semibold">{batch.display_name}</p>
 <p className="text-xs text-muted-foreground mt-1">
 {batch.source === 'business' 
 ? `Product: ${batch.inputs?.product_description || 'N/A'} • ${batch.inputs?.units_per_batch || 0} units`
 : `${batch.inputs?.items?.length || 0} items`}
 </p>
 </button>
 ))}
 </div>
 )}
 </div>
 </div>
 )}

 {/* Tray Assignment Modal */}
 {showTrayAssignmentModal && selectedModel && (
 <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
 <div className="bg-white dark:bg-slate-900 rounded-lg max-w-4xl w-full p-6 shadow-2xl max-h-[80vh] overflow-y-auto">
 <div className="flex items-center justify-between mb-4">
 <div>
 <h3 className="text-xl font-bold">Assign Products to Trays</h3>
 <p className="text-sm text-muted-foreground mt-1">
 Select which trays each product should be placed on
 </p>
 </div>
 <Button
 variant="ghost"
 size="sm"
 onClick={() => setShowTrayAssignmentModal(false)}
 >
 ✕
 </Button>
 </div>

 {/* Weighing Instructions */}
 <div className="mb-4 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border-2 border-blue-400 dark:border-blue-600">
 <div className="flex items-start gap-3">
 <Scale className="w-6 h-6 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-1" />
 <div>
 <h4 className="font-bold text-blue-900 dark:text-blue-100 mb-2">
 📋 Next Steps After Assignment
 </h4>
 <p className="text-sm text-blue-800 dark:text-blue-200 mb-2">
 Once you assign products to trays, you'll need to:
 </p>
 <ol className="text-sm text-blue-800 dark:text-blue-200 space-y-1 list-decimal list-inside">
 <li>Weigh each empty tray</li>
 <li>Load the assigned food onto each tray</li>
 <li>Weigh each tray with the wet food</li>
 <li>System will auto-calculate food weights and targets</li>
 </ol>
 </div>
 </div>
 </div>

 <div className="space-y-4 mb-6">
 {itemsToAssign.map((item, itemIndex) => (
 <div 
 key={itemIndex}
 className="p-4 border-2 border-slate-200 dark:border-slate-700 rounded-lg bg-slate-50 dark:bg-slate-800"
 >
 <div className="flex items-center justify-between mb-3">
 <div>
 <h4 className="font-bold text-green-700 dark:text-green-400">
 {item.product_name}
 </h4>
 <p className="text-xs text-muted-foreground">
 Category: {foodCategories[item.product_category]?.name || item.product_category}
 {' • '}
 Moisture: {foodCategories[item.product_category]?.moisture_percent || 0}%
 </p>
 </div>
 <div className="text-right">
 <p className="text-xs text-muted-foreground">Assigned to:</p>
 <p className="text-sm font-bold text-green-600">
 {item.assignedTrays.length > 0 
 ? `Tray ${item.assignedTrays.join(', ')}`
 : 'No trays selected'}
 </p>
 </div>
 </div>

 <div className="border-t border-slate-300 dark:border-slate-600 pt-3 mt-3">
 <Label className="text-xs font-semibold mb-2 block">
 Include on Trays:
 </Label>
 <div className="grid grid-cols-4 gap-2">
 {Array.from({ length: selectedModel.trays }, (_, index) => {
 const trayNumber = index + 1;
 const isAssigned = item.assignedTrays.includes(trayNumber);
 
 return (
 <button
 key={trayNumber}
 onClick={() => toggleTrayAssignment(itemIndex, trayNumber)}
 className={`p-3 rounded-lg border-2 font-semibold text-sm transition-all ${
 isAssigned
 ? 'border-green-500 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
 : 'border-slate-300 dark:border-slate-600 hover:border-green-400 hover:bg-green-50 dark:hover:bg-green-900/10'
 }`}
 >
 {isAssigned ? '✓ ' : ''}Tray {trayNumber}
 </button>
 );
 })}
 </div>
 </div>
 </div>
 ))}
 </div>

 <div className="flex gap-3">
 <Button
 variant="outline"
 onClick={() => setShowTrayAssignmentModal(false)}
 className="flex-1"
 >
 Cancel
 </Button>
 <Button
 onClick={applyTrayAssignments}
 className="flex-1 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700"
 disabled={itemsToAssign.every(item => item.assignedTrays.length === 0)}
 >
 <CheckCircle className="w-4 h-4 mr-2" />
 Apply Assignments
 </Button>
 </div>
 </div>
 </div>
 )}
 </div>
 </div>
 );
};

export default BatchTracker;
